import { useMemo } from 'react'
import numeral from 'numeral'
import { useFormik } from 'formik'
import { Button, Card, Notification, schemaValidation } from 'shared'
import { Account } from '../../../redux/accounts/accounts.model'
import { AccountFund } from '../../../redux/account-funds/account-funds.model'
import { WithdrawalFormFields } from '../WithdrawFundsModalGeneric'
import { withdrawalFormSchema } from '../WithdrawFundsForm.schema'
import './MFWithdrawForm.scss'
import { WithdrawalTypeEnum } from '../../../api/withdraw'
import { NextStepArrow } from './NextStepArrow'
import { MFWithdrawAmount } from './MFWithdrawAmount'
import { WithdrawBankAccount } from '../WithdrawBankAccount'

type MFWithdrawFormProps = {
  account: Account
  funds: AccountFund[]
  onCancel: () => void
  onSubmit: (values: WithdrawalFormFields) => void
  isDisable?: boolean
  values?: WithdrawalFormFields
}

const MINIMUM_ACCOUNT_BALANCE = 1000

export const MFWithdrawForm = ({
  account,
  onCancel,
  onSubmit,
  isDisable = false,
  values,
  funds,
}: MFWithdrawFormProps) => {
  const accountTotalMinusMinimumBalance = useMemo(() => {
    return numeral(account?.accountTotal)
      .subtract(MINIMUM_ACCOUNT_BALANCE)
      .value()
  }, [account])

  const initialValues: WithdrawalFormFields = {
    product: account.productExternalRef,
    accountId: account.accountID,
    withdrawalType: values?.withdrawalType || WithdrawalTypeEnum.Partial,
    withdrawalAmount: values?.withdrawalAmount || undefined,
    withdrawalFunds: values?.withdrawalFunds || [],
    useExistingBankAccount: values?.useExistingBankAccount || true,
    bankAccountName: values?.bankAccountName || null,
    bankAccountNumber: values?.bankAccountNumber || null,
  }

  const form = useFormik({
    initialValues: { ...initialValues },
    validate: schemaValidation<WithdrawalFormFields>(
      withdrawalFormSchema(accountTotalMinusMinimumBalance)
    ),
    onSubmit: () => {
      if (form.values.withdrawalType === WithdrawalTypeEnum.Full) {
        form.setFieldValue('withdrawalAmount', null)
        form.setFieldValue('withdrawalFunds', null)
      }
      if (form.values.useExistingBankAccount) {
        form.setFieldValue('bankAccountName', null)
        form.setFieldValue('bankAccountNumber', null)
      }
      onSubmit(form.values)
    },
    validateOnMount: true,
  })

  return (
    <form
      className="mf-withdraw-form"
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <Card>
        <MFWithdrawAmount
          form={form}
          funds={funds}
          minimumAccountBalance={MINIMUM_ACCOUNT_BALANCE}
        />
      </Card>

      <NextStepArrow className="mx-auto" />

      <Card>
        <WithdrawBankAccount form={form} />
      </Card>

      <Notification className="mt-md" backgroundColor="grey">
        If your withdrawal request and completed supporting documents are
        received by 1pm on any business day, your withdrawal will usually be
        processed at the unit prices calculated as at the close of the markets
        on that day. This usually means your withdrawal will be processed in 2
        business days.
      </Notification>

      <div className="flex-row mt-xl">
        <Button variant="link" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className="ml-auto"
          type="submit"
          disabled={isDisable || !form.isValid}
        >
          Continue
        </Button>
      </div>
    </form>
  )
}
